import { Alert, Button, Card, Group, Stack, Text, Title } from '@mantine/core';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { ErrorMessage } from './ErrorMessage.js';
import { useT } from '../i18n/useT.js';
import {
  usePushConfig,
  usePushDevices,
  useSubscribePush,
  useUnsubscribePush,
} from '../api/hooks.js';

/**
 * Turning notifications on for this device.
 *
 * ## Why this lives on Min konto
 *
 * A push subscription is a browser on a device and belongs to the **person**,
 * exactly like a personal access token (ADR-007). It does not change when the
 * company switcher moves, and a person with two ApS installs the app once. The
 * layout's rule is that user-scoped things are reached from the header cluster
 * and company-scoped things from the sidebar, so this belongs here and would be
 * a lie under Indstillinger.
 *
 * ## Why permission is asked on a button and not on load
 *
 * A permission prompt that appears because a page loaded is the single most
 * reliable way to have notifications blocked for ever: the answer to a question
 * nobody asked for is No, and a blocked origin cannot ask again. It is asked
 * when somebody presses a button that says what it is for.
 */

/**
 * The VAPID public key, as `PushManager.subscribe` wants it.
 *
 * `BufferSource` rather than `Uint8Array`: under TypeScript's newer lib the
 * typed array is generic over its buffer, and `Uint8Array<ArrayBufferLike>`
 * does not satisfy the DOM's `BufferSource`. Returning the underlying
 * `ArrayBuffer` is what the API actually consumes.
 */
function toApplicationServerKey(base64url: string): ArrayBuffer {
  const padded = base64url.padEnd(base64url.length + ((4 - (base64url.length % 4)) % 4), '=');
  const binary = atob(padded.replace(/-/gu, '+').replace(/_/gu, '/'));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes.buffer;
}

/** What the browser hands back, base64url, as the API expects it. */
function keyToBase64(buffer: ArrayBuffer | null): string {
  if (buffer === null) {
    return '';
  }
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/gu, '-').replace(/\//gu, '_').replace(/=+$/u, '');
}

type State =
  | 'checking'
  | 'unsupported'
  | 'denied'
  | 'subscribed'
  | 'unsubscribed';

export function PushSettings(): ReactNode {
  const t = useT();
  const config = usePushConfig();
  const devices = usePushDevices();
  const subscribe = useSubscribePush();
  const unsubscribe = useUnsubscribePush();
  const [state, setState] = useState<State>('checking');
  const [failure, setFailure] = useState<unknown>(null);

  const supported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

  useEffect(() => {
    if (!supported) {
      setState('unsupported');
      return;
    }
    if (Notification.permission === 'denied') {
      setState('denied');
      return;
    }
    void navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then((subscription) => {
        setState(subscription === null ? 'unsubscribed' : 'subscribed');
      })
      .catch(() => {
        // No worker registered, which happens on http in development. Offering
        // the button is still right: pressing it registers one.
        setState('unsubscribed');
      });
  }, [supported]);

  async function enable(): Promise<void> {
    setFailure(null);
    const publicKey = config.data?.publicKey;
    if (publicKey === null || publicKey === undefined) {
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setState(permission === 'denied' ? 'denied' : 'unsubscribed');
        return;
      }
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        // Required by every browser: a subscription that anyone could push to
        // is not offered any more.
        userVisibleOnly: true,
        applicationServerKey: toApplicationServerKey(publicKey),
      });
      await subscribe.mutateAsync({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: keyToBase64(subscription.getKey('p256dh')),
          auth: keyToBase64(subscription.getKey('auth')),
        },
      });
      setState('subscribed');
    } catch (error) {
      setFailure(error);
    }
  }

  async function disable(): Promise<void> {
    setFailure(null);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription !== null) {
        // The server first: unsubscribing locally and then failing to tell the
        // server leaves a row that is pushed to for ever with nothing listening.
        await unsubscribe.mutateAsync(subscription.endpoint);
        await subscription.unsubscribe();
      }
      setState('unsubscribed');
    } catch (error) {
      setFailure(error);
    }
  }

  return (
    <Card withBorder padding="md">
      <Stack gap="sm">
        <Title order={4}>{t('push.title')}</Title>
        <Text size="sm" c="dimmed">
          {t('push.intro')}
        </Text>

        {state === 'unsupported' && (
          <Alert color="gray" variant="light">
            {t('push.unsupported')}
          </Alert>
        )}

        {config.data?.enabled === false && (
          <Alert color="gray" variant="light">
            {t('push.notConfigured')}
          </Alert>
        )}

        {state === 'denied' && (
          <Alert color="orange" variant="light">
            {t('push.denied')}
          </Alert>
        )}

        {failure !== null && <ErrorMessage error={failure} title={t('push.failed')} />}

        {state === 'subscribed' && (
          <Group justify="space-between">
            <Text size="sm">{t('push.enabled')}</Text>
            <Button
              variant="default"
              size="compact-sm"
              loading={unsubscribe.isPending}
              onClick={() => {
                void disable();
              }}
            >
              {t('push.disable')}
            </Button>
          </Group>
        )}

        {state === 'unsubscribed' && config.data?.enabled === true && (
          <Group>
            <Button
              loading={subscribe.isPending}
              onClick={() => {
                void enable();
              }}
            >
              {t('push.enable')}
            </Button>
          </Group>
        )}

        <Stack gap={4}>
          <Text fw={500} size="sm">
            {t('push.devices')}
          </Text>
          {(devices.data?.items.length ?? 0) === 0 ? (
            <Text size="sm" c="dimmed">
              {t('push.noDevices')}
            </Text>
          ) : (
            devices.data?.items.map((device) => (
              <Text key={device.id} size="sm" {...(device.expiredAt === null ? {} : { c: 'dimmed' })}>
                {device.userAgent ?? '—'} —{' '}
                {t('push.deviceSince', { date: device.createdAt.slice(0, 10) })}
                {device.expiredAt === null ? '' : ` (${t('push.deviceExpired')})`}
              </Text>
            ))
          )}
        </Stack>
      </Stack>
    </Card>
  );
}
